/**
 * useTourChat — Chat state hook for the unified guided-tour system.
 *
 * Connects to the same `/api/v1/ai-assistant/chat` endpoint used by the AI
 * Onboarding Coach, but with a simpler context structure based on the
 * guided-tour module/flow model.
 *
 * Features:
 * - Sends messages with module + flow context (section_prompt)
 * - Parses `<ui_action>` tags from AI responses
 * - Persists chat sessions per flow via chatSessionPersistence
 * - Plays TTS from combined chat+TTS response audio (base64)
 * - Service health check (graceful degradation when AI is unavailable)
 *
 * @created September 2026 — Phase 4 of the tour consolidation.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '@/lib/authService';
import {
  saveChatSession,
  loadLatestChatSession,
  loadChatSession,
  type ChatSessionDetail,
  type ChatMessageRecord,
} from '@/lib/chatSessionPersistence';
import { extractUIActions, type ParsedUIAction } from '@/components/training/guided-tour/utils/uiActionParser';

export interface TourChatMessage {
  id: string;
  role: 'user' | 'coach';
  content: string;
  actions: ParsedUIAction[];
  timestamp: number;
  pose?: string;
}

export interface UseTourChatOptions {
  moduleKey: string;
  /** Module context string for the chat endpoint (e.g. 'finance', 'operations'). */
  moduleContext?: string;
  /** The flow whose section_prompt is used as AI context. null = module-level chat. */
  flowId: string | null;
  /** Section-specific prompt from the flow's section_prompt field. */
  sectionPrompt?: string;
  /** Module master prompt (loaded from master-prompts registry). */
  masterPrompt?: string;
  /** Whether the chat panel is open. */
  enabled: boolean;
  /** Active tab for page context. */
  activeTab?: string;
  /** Called when the AI returns a navigate action. */
  onNavigate?: (tab: string) => void;
  /** Called when the AI returns a highlight action. */
  onHighlight?: (region: string, label?: string) => void;
  /** Called when the AI returns a sandbox action (enter the training sandbox). */
  onSandbox?: () => void;
  /** Called to play base64 audio from chat responses. */
  onPlayAudio?: (base64: string) => void;
}

export interface UseTourChatResult {
  messages: TourChatMessage[];
  inputValue: string;
  setInputValue: (v: string) => void;
  isGenerating: boolean;
  sendMessage: (text?: string) => Promise<void>;
  clearChat: () => void;
  /** Restore a previously persisted session into the chat (history picker). */
  loadSession: (sessionId: string) => Promise<void>;
  serviceAvailable: boolean | null;
  /** Apply a parsed ui_action (also used by message action buttons). */
  applyAction: (action: ParsedUIAction) => void;
}

const CHAT_ENDPOINT = '/api/v1/ai-assistant/chat';

export function useTourChat(options: UseTourChatOptions): UseTourChatResult {
  const {
    moduleKey,
    moduleContext,
    flowId,
    sectionPrompt,
    masterPrompt,
    enabled,
    activeTab,
    onNavigate,
    onHighlight,
    onSandbox,
    onPlayAudio,
  } = options;

  const [messages, setMessages] = useState<TourChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(null);

  const sessionIdRef = useRef<string>(`tour-chat-${moduleKey}-${flowId ?? 'module'}-${Date.now()}`);
  const messagesRef = useRef<TourChatMessage[]>([]);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep messagesRef in sync so async callbacks read the latest messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ── Load saved session when flow changes ──────────────────────────────

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      const session = await loadLatestChatSession(moduleKey, flowId ?? undefined, 'coaching');
      if (cancelled || !session?.messages?.length) return;

      const restored: TourChatMessage[] = session.messages
        .filter((m) => m.role === 'user' || m.role === 'coach' || m.role === 'assistant')
        .map((m) => ({
          id: m.id,
          role: (m.role === 'assistant' ? 'coach' : m.role) as 'user' | 'coach',
          content: m.content,
          actions: (m.actions as unknown as ParsedUIAction[]) ?? [],
          timestamp: typeof m.timestamp === 'string' ? new Date(m.timestamp).getTime() : m.timestamp,
          pose: m.pose,
        }));

      if (restored.length > 0 && !cancelled) {
        setMessages(restored);
        if (session.sessionId) {
          sessionIdRef.current = session.sessionId;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, moduleKey, flowId]);

  // ── Persist session (debounced) ────────────────────────────────────────

  const persistSession = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const currentMsgs = messagesRef.current;
      if (currentMsgs.length === 0) return;

      saveChatSession({
        sessionId: sessionIdRef.current,
        moduleKey,
        viewId: flowId ?? undefined,
        sessionType: 'coaching',
        messages: currentMsgs.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          actions: m.actions as unknown as Array<Record<string, unknown>>,
          pose: m.pose,
        })),
      });
    }, 2000);
  }, [moduleKey, flowId]);

  // ── Apply UI actions from AI response ─────────────────────────────────

  const applyAction = useCallback(
    (action: ParsedUIAction) => {
      switch (action.type) {
        case 'navigate':
          if (action.tab && onNavigate) {
            onNavigate(action.tab);
          }
          break;
        case 'highlight':
          if (action.region && onHighlight) {
            onHighlight(action.region, action.label);
          }
          break;
        case 'scroll_to':
          if (action.region) {
            const el = document.querySelector(`[data-tour-id="${action.region}"], [data-region="${action.region}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          break;
        case 'sandbox':
          onSandbox?.();
          break;
        // open_assistant, clear — not applicable in tour chat mode
      }
    },
    [onNavigate, onHighlight, onSandbox],
  );

  // ── Send message ──────────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (text?: string) => {
      const messageText = (text ?? inputValue).trim();
      if (!messageText || isGenerating) return;

      const userMsg: TourChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: messageText,
        actions: [],
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInputValue('');
      setIsGenerating(true);

      try {
        const tokens = authService.getTokens();
        const response = await fetch(CHAT_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {}),
          },
          body: JSON.stringify({
            sessionId: sessionIdRef.current,
            moduleContext: moduleContext ?? (moduleKey === 'total-finance' ? 'finance' : 'operations'),
            message: messageText,
            tts: false, // TTS handled by useTourTTS, not the server
            context: {
              moduleKey,
              onboardingCoaching: true,
              pageContext: {
                module: moduleKey,
                activeTab: activeTab ?? '',
                openModal: null,
                recentActions: [],
              },
              viewScope: flowId ? { viewId: flowId } : null,
              sectionPrompt: sectionPrompt ?? null,
              masterPrompt: masterPrompt ?? null,
            },
          }),
        });

        if (!response.ok) {
          if (response.status === 503) setServiceAvailable(false);
          return;
        }

        const data = await response.json();
        if (!data?.success) {
          if (data?.serviceUnavailable) setServiceAvailable(false);
          return;
        }

        setServiceAvailable(true);
        const rawText = data.response ?? '';
        const { cleanedText, actions: clientParsedActions } = extractUIActions(rawText);

        // Also check for ui_actions attachment from server-side extraction
        const serverActions: ParsedUIAction[] = (data.attachments ?? [])
          .filter((att: { type: string; data?: { actions?: ParsedUIAction[] } }) => att.type === 'ui_actions')
          .flatMap((att: { data?: { actions?: ParsedUIAction[] } }) => att.data?.actions ?? []);

        const actions = serverActions.length > 0 ? serverActions : clientParsedActions;

        const coachMessage: TourChatMessage = {
          id: `coach-${Date.now()}`,
          role: 'coach',
          content: cleanedText,
          actions,
          timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, coachMessage]);

        // Play audio from the combined chat+TTS response
        if (data.audio && onPlayAudio) {
          onPlayAudio(data.audio);
        }

        // Auto-apply the first non-sandbox action — navigation/highlight are
        // the "the coach moved the page for you" behaviour. Sandbox entry is
        // never auto-applied: it reloads the page, so it stays an explicit
        // click on the action chip rendered under the message.
        const autoAction = actions.find((a) => a.type !== 'sandbox');
        if (autoAction) {
          applyAction(autoAction);
        }

        persistSession();
      } catch {
        // Silent failure — chat is non-critical
      } finally {
        setIsGenerating(false);
      }
    },
    [inputValue, isGenerating, moduleKey, moduleContext, activeTab, flowId, sectionPrompt, masterPrompt, onPlayAudio, applyAction, persistSession],
  );

  // ── Restore a persisted session (history picker) ───────────────────────

  const restoreMessages = useCallback((records: ChatMessageRecord[]): TourChatMessage[] => {
    return records
      .filter((m) => m.role === 'user' || m.role === 'coach' || m.role === 'assistant')
      .map((m) => ({
        id: m.id,
        role: (m.role === 'assistant' ? 'coach' : m.role) as 'user' | 'coach',
        content: m.content,
        actions: (m.actions as unknown as ParsedUIAction[]) ?? [],
        timestamp: typeof m.timestamp === 'string' ? new Date(m.timestamp).getTime() : m.timestamp,
        pose: m.pose,
      }));
  }, []);

  const loadSession = useCallback(
    async (sessionId: string) => {
      const session = await loadChatSession(sessionId);
      if (!session?.messages?.length) return;
      const restored = restoreMessages(session.messages);
      if (restored.length === 0) return;
      setMessages(restored);
      sessionIdRef.current = session.sessionId;
    },
    [restoreMessages],
  );

  // ── Clear chat ──────────────────────────────────────────────────────────

  const clearChat = useCallback(() => {
    setMessages([]);
    sessionIdRef.current = `tour-chat-${moduleKey}-${flowId ?? 'module'}-${Date.now()}`;
    persistSession();
  }, [moduleKey, flowId, persistSession]);

  // ── Cleanup ─────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return {
    messages,
    inputValue,
    setInputValue,
    isGenerating,
    sendMessage,
    clearChat,
    loadSession,
    serviceAvailable,
    applyAction,
  };
}
